import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const EndSession = () => {
  const { sessionId } = useParams();
  const [popupState, setPopupState] = useState<'hidden' | 'loading' | 'success' | 'error' | 'timeout'>('hidden');
  const [checklist, setChecklist] = useState({
    tidy: false,
    belongings: false,
    door: false,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSessionComplete, setIsSessionComplete] = useState<boolean | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [sessionCheckError, setSessionCheckError] = useState<string | null>(null);

  useEffect(() => {
    const checkSessionStatus = async () => {
      if (!sessionId) {
        setSessionCheckError('No session ID provided');
        setIsCheckingSession(false);
        return;
      }
      
      try {
        const response = await fetch(
          `https://qwipsvc.up.railway.app/api/is-session-complete?session_id=${sessionId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setIsSessionComplete(data.is_complete);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setSessionCheckError(errorData.message || `Failed to check session status (${response.status})`);
        }
      } catch (error) {
        console.error('Error checking session status:', error);
        setSessionCheckError('Unable to connect to server. Please try again later.');
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSessionStatus();
  }, [sessionId]);

  const allChecked = checklist.tidy && checklist.belongings && checklist.door;

  const handleCheckboxChange = (key: keyof typeof checklist) => {
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleEndSession = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setPopupState('loading');

    try {
      const response = await fetch('https://qwipsvc.up.railway.app/api/end-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
        }),
      });

      if (response.ok) {
        setPopupState('success');
      } else {
        setPopupState('error');
      }
    } catch (error) {
      console.error('Error ending session:', error);
      setPopupState('error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#374151' }}></div>
          <p className="text-muted-foreground">Checking session status...</p>
        </motion.div>
      </div>
    );
  }

  if (sessionCheckError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Error</h1>
          <p className="text-muted-foreground text-lg">
            {sessionCheckError}
          </p>
        </motion.div>
      </div>
    );
  }

  if (isSessionComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center"
        >
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(55, 65, 81, 0.2)' }}>
            <Check className="w-8 h-8" style={{ color: '#374151' }} />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Session Already Ended</h1>
          <p className="text-muted-foreground text-lg">
            This session has already been terminated. Thank you for using qwip!
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-card rounded-lg shadow-lg p-8"
      >
        <h1 className="text-3xl font-bold text-foreground mb-8">End Session</h1>

        <div className="space-y-4 mb-8">
          <ChecklistItem
            label="I have left the pod tidy"
            checked={checklist.tidy}
            onChange={() => handleCheckboxChange("tidy")}
          />
          <ChecklistItem
            label="I have gathered all my belongings"
            checked={checklist.belongings}
            onChange={() => handleCheckboxChange("belongings")}
          />
          <ChecklistItem
            label="I have closed the door properly"
            checked={checklist.door}
            onChange={() => handleCheckboxChange("door")}
          />
        </div>

        <button
          onClick={handleEndSession}
          disabled={!allChecked || isProcessing}
          className={`w-full py-3 rounded-lg font-semibold transition-all ${
            allChecked && !isProcessing
              ? "text-white hover:opacity-90"
              : "bg-muted text-muted-foreground cursor-not-allowed"
          }`}
          style={allChecked && !isProcessing ? { backgroundColor: '#374151' } : undefined}
        >
          {isProcessing ? 'Processing...' : 'End Booking'}
        </button>
      </motion.div>

      {popupState !== 'hidden' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50"
          onClick={() => popupState !== 'loading' && setPopupState('hidden')}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-card rounded-lg p-8 max-w-sm w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {popupState === 'loading' && (
              <>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#374151' }}></div>
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Ending Session...
                </h2>
                <p className="text-muted-foreground">
                  Please wait while we process your request
                </p>
              </>
            )}

            {popupState === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(55, 65, 81, 0.2)' }}>
                  <Check className="w-8 h-8" style={{ color: '#374151' }} />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Session Ended
                </h2>
                <p className="text-muted-foreground mb-6">
                  Thank you for using our pod!
                </p>
                <button
                  onClick={() => setPopupState('hidden')}
                  className="text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#374151' }}
                >
                  Close
                </button>
              </>
            )}

            {(popupState === 'error' || popupState === 'timeout') && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {popupState === 'timeout' ? 'Request Timed Out' : 'Error'}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {popupState === 'timeout' 
                    ? 'The request took too long to complete. Please contact support for assistance.'
                    : 'Failed to end session. Please contact support for assistance.'}
                </p>
                <button
                  onClick={() => setPopupState('hidden')}
                  className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

const ChecklistItem = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div
        className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
          checked
            ? "border-2"
            : "border-muted-foreground"
        }`}
        style={checked ? { backgroundColor: '#374151', borderColor: '#374151' } : undefined}
        onMouseEnter={(e) => {
          if (!checked) {
            e.currentTarget.style.borderColor = '#374151';
          }
        }}
        onMouseLeave={(e) => {
          if (!checked) {
            e.currentTarget.style.borderColor = '';
          }
        }}
      >
        {checked && <Check className="w-4 h-4 text-white" />}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <span className={`text-foreground ${checked ? "line-through opacity-60" : ""}`}>
        {label}
      </span>
    </label>
  );
};

export default EndSession;
